/**
 * ============================================================
 * PORTFOLIO TEMPLATE - CONFIGURATION CENTRALE
 * ============================================================
 * 
 * Ce fichier est le SEUL endroit à modifier pour personnaliser
 * votre portfolio. Remplacez les valeurs par les vôtres.
 * 
 * This file is the ONLY place you need to edit to customize
 * your portfolio. Replace the values with your own.
 * ============================================================
 */

import { Lightbulb, Users, BarChart3, Shield, TrendingUp, Palette, Megaphone, Brain, Target, type LucideIcon } from "lucide-react";
import brandStrategyImg from "@/assets/projects/brand-strategy.jpg";
import digitalCommunicationImg from "@/assets/projects/digital-communication.jpg";
import growthStrategyImg from "@/assets/projects/growth-strategy.jpg";
import personalBrandingImg from "@/assets/projects/personal-branding-guide.jpg";
import personalBrandingMobileImg from "@/assets/projects/personal-branding-mobile.jpg";

// ============================================================
// 1. PERSONAL INFO
// ============================================================

export const personalInfo = {
  firstName: "Sophie",
  lastName: "Martin",
  role: {
    fr: "Consultante en Stratégie & Communication",
    en: "Strategy & Communication Consultant",
  },
  description: {
    fr: "Consultante passionnée spécialisée dans la stratégie de marque et la communication digitale. J'accompagne les entreprises et les indépendants dans leur développement avec un focus sur l'impact et les résultats concrets.",
    en: "Passionate consultant specialized in brand strategy and digital communication. I help businesses and freelancers grow with a focus on impact and concrete results.",
  },
  email: "sophie.martin@example.com",
  location: "Paris, France",
  linkedin: "https://linkedin.com/in/sophiemartin",
  github: "",
  website: "https://sophiemartin.com",
  profileImage: "/profile.jpg",
  profileImageWebp: "/profile.webp",
  availability: {
    available: true,
    fr: "Disponible pour de nouveaux projets",
    en: "Available for new projects",
    subtext: {
      fr: "Prochaine disponibilité : immédiate",
      en: "Next availability: immediate",
    },
  },
  cvFilename: {
    fr: "CV_Sophie_Martin_FR.pdf",
    en: "CV_Sophie_Martin_EN.pdf",
  },
};

// ============================================================
// 2. SEO & META
// ============================================================

export const seoConfig = {
  siteUrl: "https://your-portfolio.com",
  title: {
    fr: `${personalInfo.firstName} ${personalInfo.lastName} - ${personalInfo.role.fr}`,
    en: `${personalInfo.firstName} ${personalInfo.lastName} - ${personalInfo.role.en}`,
  },
  description: {
    fr: personalInfo.description.fr,
    en: personalInfo.description.en,
  },
  keywords: "consultant, stratégie, communication, branding, portfolio",
  ogImage: "/og-image.png",
  twitterHandle: "",
  locale: "fr_FR",
};

// ============================================================
// 3. SECTIONS - Enable/disable sections
// ============================================================

export const sections = {
  hero: true,
  companyLogos: true,
  skills: true,
  whyHireMe: true,
  fitScore: true,
  experience: true,
  caseStudy: true,
  portfolio: true,
  personalProjects: true,
  certifications: true,
  currentlyLearning: true,
  contact: true,
  downloadCv: true,
  floatingContact: true,
};

// ============================================================
// 4. NAVIGATION
// ============================================================

export const navigation = {
  logo: {
    text: personalInfo.firstName,
    highlight: personalInfo.firstName.charAt(0),
    rest: personalInfo.firstName.slice(1),
  },
  items: [
    { labelKey: "nav.skills", href: "#skills", visible: sections.skills },
    { labelKey: "nav.experience", href: "#experience", visible: sections.experience },
    { labelKey: "nav.portfolio", href: "#portfolio", visible: sections.portfolio },
    { labelKey: "nav.fitScore", href: "#fit-score", visible: sections.fitScore },
    { labelKey: "nav.certifications", href: "#certifications", visible: sections.certifications },
    { labelKey: "nav.contact", href: "#contact", visible: sections.contact },
  ].filter(item => item.visible),
};

// ============================================================
// 5. HERO - Badges & Expertise Pillars
// ============================================================

export const heroBadges = [
  {
    icon: "Award" as const,
    label: { fr: "Stratégie de marque", en: "Brand Strategy" },
    sublabel: { fr: "15+ projets", en: "15+ Projects" },
    color: "from-blue-500 to-indigo-600",
  },
  {
    icon: "Zap" as const,
    label: { fr: "8+ ans XP", en: "8+ Years XP" },
    sublabel: { fr: "Conseil & stratégie", en: "Consulting & strategy" },
    color: "from-cyan-500 to-blue-600",
  },
  {
    icon: "Target" as const,
    label: { fr: "Résultats concrets", en: "Concrete Results" },
    sublabel: { fr: "+40% visibilité moy.", en: "+40% avg. visibility" },
    color: "from-purple-500 to-violet-600",
  },
  {
    icon: "Shield" as const,
    label: { fr: "Accompagnement", en: "Guidance" },
    sublabel: { fr: "Sur-mesure", en: "Tailored" },
    color: "from-emerald-500 to-green-600",
  },
];

export const techStack = [
  { name: "Stratégie", color: "#3B82F6" },
  { name: "Branding", color: "#8B5CF6" },
  { name: "Communication", color: "#06B6D4" },
  { name: "Marketing Digital", color: "#10B981" },
  { name: "Gestion de Projet", color: "#F59E0B" },
  { name: "Analyse & KPIs", color: "#EF4444" },
];

// ============================================================
// 6. SKILLS
// ============================================================

export interface SkillConfig {
  icon: LucideIcon;
  titleKey: string;
  descriptionKey: string;
  technologies: string[];
  color: string;
  iconColor: string;
  radarValue: number;
}

export const skills: SkillConfig[] = [
  {
    icon: Lightbulb,
    titleKey: "skills.strategy.title",
    descriptionKey: "skills.strategy.description",
    technologies: ["Positionnement", "Business Plan", "Veille concurrentielle", "Innovation"],
    color: "from-blue-500/20 to-cyan-500/20",
    iconColor: "text-blue-500",
    radarValue: 95,
  },
  {
    icon: Megaphone,
    titleKey: "skills.communication.title",
    descriptionKey: "skills.communication.description",
    technologies: ["Réseaux sociaux", "Relations presse", "Storytelling", "Événementiel"],
    color: "from-purple-500/20 to-pink-500/20",
    iconColor: "text-purple-500",
    radarValue: 90,
  },
  {
    icon: Palette,
    titleKey: "skills.branding.title",
    descriptionKey: "skills.branding.description",
    technologies: ["Identité visuelle", "Charte graphique", "Naming", "Direction artistique"],
    color: "from-orange-500/20 to-red-500/20",
    iconColor: "text-orange-500",
    radarValue: 88,
  },
  {
    icon: BarChart3,
    titleKey: "skills.analytics.title",
    descriptionKey: "skills.analytics.description",
    technologies: ["KPIs", "Tableaux de bord", "ROI", "Reporting"],
    color: "from-emerald-500/20 to-green-500/20",
    iconColor: "text-emerald-500",
    radarValue: 85,
  },
  {
    icon: TrendingUp,
    titleKey: "skills.growth.title",
    descriptionKey: "skills.growth.description",
    technologies: ["Acquisition", "Fidélisation", "Partenariats", "Référencement"],
    color: "from-amber-500/20 to-yellow-500/20",
    iconColor: "text-amber-500",
    radarValue: 82,
  },
  {
    icon: Users,
    titleKey: "skills.management.title",
    descriptionKey: "skills.management.description",
    technologies: ["Gestion d'équipe", "Pilotage", "Formation", "Relation client"],
    color: "from-indigo-500/20 to-violet-500/20",
    iconColor: "text-indigo-500",
    radarValue: 80,
  },
];

// ============================================================
// 7. EXPERIENCE
// ============================================================

export interface ExperienceConfig {
  titleKey: string;
  companyKey: string;
  companyUrl: string | null;
  companyLogo: string | null;
  companyColor: string;
  periodKey: string;
  duration: { fr: string; en: string };
  descriptionKey: string;
  missionKeys: string[];
  technologies: string[];
  sector: { fr: string; en: string };
  current: boolean;
  startYear: number;
}

export const experiences: ExperienceConfig[] = [
  {
    titleKey: "experience.job1.title",
    companyKey: "experience.job1.company",
    companyUrl: null,
    companyLogo: null,
    companyColor: "#3B82F6",
    periodKey: "experience.job1.period",
    duration: { fr: "En cours", en: "Ongoing" },
    descriptionKey: "experience.job1.description",
    missionKeys: [
      "experience.job1.mission1",
      "experience.job1.mission2",
      "experience.job1.mission3",
      "experience.job1.mission4",
    ],
    technologies: ["Stratégie", "Branding", "Marketing Digital", "Gestion de Projet"],
    sector: { fr: "Conseil", en: "Consulting" },
    current: true,
    startYear: 2024,
  },
  {
    titleKey: "experience.job2.title",
    companyKey: "experience.job2.company",
    companyUrl: null,
    companyLogo: null,
    companyColor: "#10B981",
    periodKey: "experience.job2.period",
    duration: { fr: "3 ans", en: "3 years" },
    descriptionKey: "experience.job2.description",
    missionKeys: [
      "experience.job2.mission1",
      "experience.job2.mission2",
      "experience.job2.mission3",
      "experience.job2.mission4",
    ],
    technologies: ["Communication", "Relations Presse", "Événementiel", "Réseaux Sociaux"],
    sector: { fr: "Communication", en: "Communication" },
    current: false,
    startYear: 2021,
  },
];

// ============================================================
// 8. COMPANIES / CLIENTS (Logo carousel)
// ============================================================

export interface CompanyLogo {
  name: string;
  logo: string;
}

export const companyLogos: CompanyLogo[] = [];

// ============================================================
// 9. PORTFOLIO PROJECTS
// ============================================================

export interface ProjectConfig {
  icon: LucideIcon;
  titleKey: string;
  descriptionKey: string;
  tags: string[];
  color: string;
  image: string;
  isCurrent?: boolean;
}

export const projects: ProjectConfig[] = [
  {
    icon: Lightbulb,
    titleKey: "portfolio.project1.title",
    descriptionKey: "portfolio.project1.description",
    tags: ["Stratégie", "Branding", "Positionnement", "Lancement"],
    color: "from-indigo-600 to-blue-500",
    image: brandStrategyImg,
    isCurrent: true,
  },
  {
    icon: Megaphone,
    titleKey: "portfolio.project2.title",
    descriptionKey: "portfolio.project2.description",
    tags: ["Communication", "Réseaux Sociaux", "Contenu", "Engagement"],
    color: "from-green-500 to-emerald-500",
    image: digitalCommunicationImg,
  },
  {
    icon: TrendingUp,
    titleKey: "portfolio.project3.title",
    descriptionKey: "portfolio.project3.description",
    tags: ["Croissance", "Acquisition", "Fidélisation", "KPIs"],
    color: "from-orange-500 to-red-500",
    image: growthStrategyImg,
  },
];

// ============================================================
// 10. CERTIFICATIONS
// ============================================================

export interface CertificationConfig {
  name: string;
  code: string;
  dateKey: string;
  status: "obtained" | "in-progress";
}

export const certifications: CertificationConfig[] = [
  {
    name: "Google Analytics Certified",
    code: "GA4",
    dateKey: "certifications.date1",
    status: "obtained",
  },
];

export interface EducationConfig {
  titleKey: string;
  subtitleKey: string;
  levelKey: string;
}

export const education: EducationConfig[] = [
  {
    titleKey: "certifications.edu1.title",
    subtitleKey: "certifications.edu1.subtitle",
    levelKey: "certifications.edu1.level",
  },
];

export interface LanguageSkill {
  nameKey: string;
  levelKey: string;
  proficiency: number;
}

export const languageSkills: LanguageSkill[] = [
  { nameKey: "certifications.french", levelKey: "certifications.french.level", proficiency: 100 },
  { nameKey: "certifications.english", levelKey: "certifications.english.level", proficiency: 75 },
];

// ============================================================
// 11. WHY HIRE ME - Value Propositions
// ============================================================

export interface ValuePropConfig {
  icon: LucideIcon;
  title: { fr: string; en: string };
  description: { fr: string; en: string };
  highlights: string[];
  gradient: string;
  iconColor: string;
}

export const valueProps: ValuePropConfig[] = [
  {
    icon: Lightbulb,
    title: { fr: "Vision stratégique", en: "Strategic Vision" },
    description: {
      fr: "Une approche globale pour positionner votre marque et atteindre vos objectifs",
      en: "A comprehensive approach to position your brand and reach your goals",
    },
    highlights: ["Positionnement", "Business Plan", "Veille", "Innovation"],
    gradient: "from-cyan-500/20 to-blue-600/20",
    iconColor: "text-cyan-500",
  },
  {
    icon: Megaphone,
    title: { fr: "Communication impactante", en: "Impactful Communication" },
    description: {
      fr: "Des messages percutants qui touchent votre audience cible",
      en: "Powerful messages that reach your target audience",
    },
    highlights: ["Storytelling", "Réseaux sociaux", "Relations presse", "Contenu"],
    gradient: "from-green-500/20 to-emerald-600/20",
    iconColor: "text-green-500",
  },
];

export interface EnterpriseFitConfig {
  company: { fr: string; en: string };
  subtitle?: string;
  logo: string;
  reason: { fr: string; en: string };
  status: "strong" | "solid" | "developing";
}

export const enterpriseFit: EnterpriseFitConfig[] = [
  {
    company: { fr: "PME & Indépendants", en: "SMBs & Freelancers" },
    logo: "🏢",
    reason: {
      fr: "Accompagnement sur-mesure, résultats rapides",
      en: "Tailored guidance, fast results",
    },
    status: "strong",
  },
  {
    company: { fr: "Startups", en: "Startups" },
    subtitle: "Seed → Series A",
    logo: "🚀",
    reason: {
      fr: "Positionnement de marque, stratégie de lancement",
      en: "Brand positioning, launch strategy",
    },
    status: "solid",
  },
  {
    company: { fr: "Grands Comptes", en: "Enterprise" },
    logo: "🏛️",
    reason: {
      fr: "Communication corporate, gestion de projets complexes",
      en: "Corporate communication, complex project management",
    },
    status: "developing",
  },
];

// ============================================================
// 12. FIT SCORE ROLES
// ============================================================

export interface FitScoreRole {
  id: string;
  title: { fr: string; en: string };
  company: string;
  salary: { fr: string; en: string };
  fitLabel: { fr: string; en: string };
  fitLevel: "excellent" | "good" | "partial";
  requirements: {
    skill: string;
    level: "strong" | "solid" | "learning" | "gap";
    category: "core" | "bonus" | "nice-to-have";
  }[];
}

export const fitScoreRoles: FitScoreRole[] = [
  {
    id: "brand-strategist",
    title: { fr: "Stratège de Marque", en: "Brand Strategist" },
    company: "Agence",
    salary: { fr: "45-60K €", en: "45-60K €" },
    fitLabel: { fr: "Très bon fit", en: "Very good fit" },
    fitLevel: "excellent",
    requirements: [
      { skill: "Positionnement", level: "strong", category: "core" },
      { skill: "Branding", level: "strong", category: "core" },
      { skill: "Analyse marché", level: "strong", category: "core" },
      { skill: "Storytelling", level: "solid", category: "bonus" },
    ],
  },
  {
    id: "communication-manager",
    title: { fr: "Responsable Communication", en: "Communication Manager" },
    company: "Entreprise",
    salary: { fr: "50-65K €", en: "50-65K €" },
    fitLabel: { fr: "Bon fit", en: "Good fit" },
    fitLevel: "good",
    requirements: [
      { skill: "Communication", level: "strong", category: "core" },
      { skill: "Réseaux sociaux", level: "solid", category: "core" },
      { skill: "Relations presse", level: "solid", category: "core" },
      { skill: "Management", level: "learning", category: "bonus" },
    ],
  },
];

// ============================================================
// 13. CASE STUDY (Current Project)
// ============================================================

export const caseStudy = {
  title: { fr: "Refonte stratégique de marque", en: "Strategic Brand Overhaul" },
  client: "Maison Lumière",
  location: "Paris, France",
  industry: { fr: "Luxe & Lifestyle", en: "Luxury & Lifestyle" },
  period: { fr: "Depuis janvier 2024", en: "Since January 2024" },
  role: "Consultante Stratégie",
  context: {
    fr: "Maison Lumière est une marque française de décoration haut de gamme souhaitant se repositionner sur le marché du luxe accessible. Le projet vise à repenser l'identité de marque et la stratégie de communication pour toucher une nouvelle clientèle.",
    en: "Maison Lumière is a French high-end home décor brand looking to reposition itself in the accessible luxury market. The project aims to rethink the brand identity and communication strategy to reach a new audience.",
  },
  responsibilities: {
    fr: [
      "Audit complet de l'image de marque et du positionnement actuel",
      "Définition de la nouvelle stratégie de communication multicanal",
      "Conception de la charte éditoriale et du storytelling de marque",
      "Pilotage du lancement de la nouvelle identité visuelle",
      "Suivi des KPIs et ajustements stratégiques continus",
    ],
    en: [
      "Complete brand image and positioning audit",
      "Definition of the new multichannel communication strategy",
      "Design of editorial guidelines and brand storytelling",
      "Oversight of the new visual identity launch",
      "KPI tracking and continuous strategic adjustments",
    ],
  },
  technologies: ["Stratégie de marque", "Communication", "Storytelling", "Analyse KPIs", "Direction artistique"],
};

// ============================================================
// 14. PERSONAL PROJECTS
// ============================================================

export const personalProject = {
  title: { fr: "Le Guide du Personal Branding", en: "The Personal Branding Guide" },
  description: {
    fr: "Un guide complet et interactif pour aider les professionnels à construire leur marque personnelle et se démarquer dans leur secteur.",
    en: "A comprehensive, interactive guide to help professionals build their personal brand and stand out in their industry.",
  },
  highlights: {
    fr: [
      "Méthode en 5 étapes pour définir son positionnement unique",
      "Templates et exemples concrets pour chaque secteur",
      "Conseils pour développer sa présence en ligne",
      "Suivi de progression avec objectifs mesurables",
    ],
    en: [
      "5-step method to define your unique positioning",
      "Templates and concrete examples for each industry",
      "Tips for developing your online presence",
      "Progress tracking with measurable goals",
    ],
  },
  tags: ["Personal Branding", "Stratégie", "Communication", "Coaching"],
  url: "https://personal-branding-guide.com/",
  image: personalBrandingImg,
  mobileImage: personalBrandingMobileImg,
};

// ============================================================
// 15. CURRENTLY LEARNING
// ============================================================

export interface LearningItemConfig {
  name: string;
  category: { fr: string; en: string };
  status: { fr: string; en: string };
  icon: string;
  relevance: { fr: string; en: string };
  link?: string;
}

export const learningItems: LearningItemConfig[] = [
  {
    name: "Intelligence Artificielle & Marketing",
    category: { fr: "Formation", en: "Training" },
    status: { fr: "En cours", en: "Ongoing" },
    icon: "🤖",
    relevance: { fr: "Automatisation & personnalisation", en: "Automation & personalization" },
  },
  {
    name: "Certification HubSpot Inbound Marketing",
    category: { fr: "Certification", en: "Certification" },
    status: { fr: "Planifié", en: "Planned" },
    icon: "📊",
    relevance: { fr: "Stratégie d'acquisition", en: "Acquisition strategy" },
    link: "https://academy.hubspot.com/",
  },
];

// ============================================================
// 16. SOCIAL LINKS (Footer & Contact)
// ============================================================

export const socialLinks = [
  { platform: "linkedin", url: personalInfo.linkedin, label: "LinkedIn" },
  ...(personalInfo.website
    ? [{ platform: "website", url: personalInfo.website, label: "Website" }]
    : []),
];

// ============================================================
// 17. THEME CUSTOMIZATION
// ============================================================

export const theme = {
  defaultTheme: "dark" as const,
  enableThemeToggle: true,
  enableLanguageToggle: true,
  defaultLanguage: "fr" as const,

  // ── COLOR PALETTE — Indigo Luxe + Coral Conversion ──
  colors: {
    light: {
      primary: "252 80% 55%",
      accent: "15 85% 55%",
      background: "40 25% 98%",
      foreground: "240 25% 10%",
      card: "0 0% 100%",
      muted: "240 15% 94%",
      mutedForeground: "240 8% 42%",
      border: "240 12% 90%",
    },
    dark: {
      primary: "252 85% 65%",
      accent: "15 90% 60%",
      background: "240 20% 4%",
      foreground: "240 10% 96%",
      card: "240 18% 7%",
      muted: "240 15% 12%",
      mutedForeground: "240 8% 55%",
      border: "240 15% 14%",
    },
  },
};
